import {
  CloudflareTurnTokenResponse,
  createCloudflareAuthenticatedTurnTokenCreds,
  createCloudflarePublicTurnTokenCreds
} from '@goldcast/api/backend';
import { StreamTypeEnum as StreamTypeEnumApi } from '@goldcast/api/content';
import { AutoplayErrorDialog, GenericMessageDialog } from 'uicomponents';

import { getAgoraRTC, getAgoraRTCSync, getDenoiserExtension } from './agoraLoader';
import { backendHaFetcher } from './backendHaFetcher';
import { handlePeerConnectionTimeout, verifyAndSaveProxyMode } from './cloudProxy.service';
import DialogService from './dialogs/DialogService';
import NotificationService from './dialogs/notifications';
import { getAgoraToken } from './event.service';
import { getValueFromSessionStorage, saveValueToSessionStorage } from './localStorage.service';
import loggerService from './logger.service';
import { classifyDiagnosticError } from './diagnostics';
import { getTrackingValues, setUsingCloudProxy } from './mediaTracking.utils';
import { currentUserHasStageRequest, updateOnStageRequest } from './stageControl.service';
import { LocalTrack, LocalTrackStatus, trackMap } from './stream.service';
import trackerService from './tracker.service';
import { UserRole } from './user.service';
import { retry } from './utils';

import type {
  IAgoraRTCError,
  ConnectionDisconnectedReason,
  ConnectionState,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ILocalAudioTrack,
  ILocalTrack,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
  ITrack,
  NetworkQuality,
  SDK_MODE,
  UID
} from './agora.types';

import analytics from '@/analytics';
import { AGORA, AGORA_CONNECTION_TIMEOUT, AGORA_EVENTS, getEnv, LOCAL_STORAGE_KEYS } from '@/constants';
import store from '@/store';
import { Broadcast, User } from '@/store/modules/event/types';
import {
  ProgressiveCloudProxyStatus,
  SDRecordingStatus,
  TurnServerFallbackStatus
} from '@/store/modules/mediaroom/types';
import { collectNetworkQualityDataPoints } from '@/utils/networkQuality';
import { exponentialRetry } from '@/utils/utils';
import JoiningDelayDailog from '@/views/event/TechCheck/JoiningDelayDailog.vue';

const NOTIFICATIONS_THRESHOLD = 3;
let noOfAgoraDisconnectioned = 0;
let noOfLowAudioNotifications = 0;
let noOfSendLowBitrateNotifications = 0;
const failingSubscriptionUids = new Set<string>();

function mapConnectionStatesToSDStatus(
  peerConnectionState: RTCPeerConnectionState,
  connectionState: ConnectionState,
  connectionDisconnectedReason?: ConnectionDisconnectedReason
): SDRecordingStatus {
  if (peerConnectionState === 'connected' && connectionState === 'CONNECTED') {
    return SDRecordingStatus.CONNECTED;
  }

  if (connectionState === 'DISCONNECTED' && connectionDisconnectedReason === 'LEAVE') {
    return SDRecordingStatus.IDLE;
  }

  if (
    peerConnectionState === 'disconnected' ||
    peerConnectionState === 'failed' ||
    peerConnectionState === 'closed' ||
    connectionState === 'DISCONNECTED' ||
    connectionState === 'DISCONNECTING'
  ) {
    return SDRecordingStatus.FAILED;
  }

  if (peerConnectionState === 'connecting' || connectionState === 'CONNECTING' || connectionState === 'RECONNECTING') {
    return SDRecordingStatus.CONNECTING;
  }

  return SDRecordingStatus.IDLE;
}

function updateSDRecordingStatus(
  feed: 'user' | 'screenshare',
  channelName: string,
  isScreenShare: boolean,
  connectionDisconnectedReason?: ConnectionDisconnectedReason
) {
  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    return;
  }

  const channelState = store.state.mediaroom.channelPeerConnectionStates[channelName];
  const mediaRoom = store.state.mediaroom.mediaRooms[channelName];

  if (!channelState || !mediaRoom) {
    return;
  }

  const peerConnectionState = channelState.peerConnectionState || 'new';
  const connectionState = isScreenShare ? mediaRoom.screenStatus : mediaRoom.status;
  const disconnectReason =
    connectionDisconnectedReason ||
    (isScreenShare ? mediaRoom.screenConnectionDisconnectedReason : mediaRoom.connectionDisconnectedReason);

  if (!connectionState) {
    return;
  }

  const sdStatus = mapConnectionStatesToSDStatus(peerConnectionState, connectionState, disconnectReason);

  store.commit('mediaroom/setSDRecordingStatus', {
    feed,
    status: sdStatus
  });
}

let agoraHandlersInitialized = false;

/**
 * Initializes Agora global handlers. Called lazily when Agora SDK is first loaded.
 */
async function initializeAgoraHandlers() {
  if (agoraHandlersInitialized) {
    return;
  }

  const AgoraRTC = await getAgoraRTC();
  AgoraRTC.onAutoplayFailed = () => {
    store.commit('event/stage/toggleMuted');
    const res = DialogService.show(AutoplayErrorDialog);
    res.promise.then(() => {
      store.commit('event/stage/toggleMuted');
    });
  };

  // Update noise cancellation compatibility now that SDK is loaded
  const denoiser = getDenoiserExtension();
  if (denoiser) {
    store.commit('mediaroom/setNoiseCancellationCompatible', denoiser.checkCompatibility());
  }

  agoraHandlersInitialized = true;
}

/**
 * Stores reference of popups shown to user while joining channel
 */
let clientJoinRetries = 0;

const peerConnectionTimeouts = new Map<string, NodeJS.Timeout>();
const peerConnectionStartTimes = new Map<string, number>();

let remoteSignalTrackerInterval: NodeJS.Timeout | null = null;

export enum MediaUserRole {
  audience = 'audience',
  host = 'host'
}

export interface MediaRoom {
  client: IAgoraRTCClient;
  screenShareClient: IAgoraRTCClient;
  mode: SDK_MODE;
  /**
   * channelId|channelSuffix
   */
  channelName: string;
  remoteStreams: {
    [uid: string]: RemoteStream;
  };
  userRole: MediaUserRole;
  isMuted: boolean;
  activeSpeakerId: UID;
  speakerOutDeviceId: string;
  connectionStrength: number;
  status?: ConnectionState;
  connectionDisconnectedReason?: ConnectionDisconnectedReason;
  screenStatus?: ConnectionState;
  screenConnectionDisconnectedReason?: ConnectionDisconnectedReason;
  desiredStatus?: ConnectionState;
  id: Number;
  label?: any;
}

export interface RemoteMediaStream extends RemoteStream {
  loading?: Boolean;
  showControls?: Boolean;
  signalStrength?: Number;
  id: String;
}

export type AgoraToken = {
  token: string;
  suffix: string;
  room: string;
  channel_suffix: string;
};
export interface RtcDevices {
  microphones: MediaDeviceInfo[];
  cameras: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
}

type RemoteStream = {
  /**
   * Missing audioTrack means that the stream audio is muted
   */
  audioTrack?: IRemoteAudioTrack;
  /**
   * Missing videoTrack means that the stream video is muted
   */
  videoTrack?: IRemoteVideoTrack;
  user?: any;
  networkQuality?: NetworkQuality;
  audioLevel?: number;
};

export async function enableAgoraLogs() {
  const AgoraRTC = await getAgoraRTC();
  AgoraRTC.enableLogUpload();
  AgoraRTC.setLogLevel(0);
}

async function createMediaRoom(
  channelName: MediaRoom['channelName'],
  userRole: MediaUserRole,
  mode: SDK_MODE
): Promise<MediaRoom> {
  // Load Agora SDK lazily and initialize handlers
  const AgoraRTC = await getAgoraRTC();
  await initializeAgoraHandlers();

  // Enable Agora logs (always enabled)
  await enableAgoraLogs();

  // If we want to change h264, do check screen share tab on Chrome, it takes a long time to load screen
  const codec = 'vp8';
  return {
    client: AgoraRTC.createClient({
      mode,
      codec
    }),
    screenShareClient: AgoraRTC.createClient({
      mode,
      codec
    }),
    mode,
    channelName,
    remoteStreams: {},
    isMuted: false,
    activeSpeakerId: '',
    speakerOutDeviceId: '',
    connectionStrength: 0,
    userRole,
    id: new Date().getTime()
  };
}

/**
 * Sets up Cloudflare TURN servers as a fallback when proxy is exhausted
 * @param client - Agora RTC client to configure
 */
export async function setupTurnServerFallback(
  client: IAgoraRTCClient,
  channelName: string,
  preferredTurnServerUrl: string
) {
  try {
    if (preferredTurnServerUrl) {
      loggerService.log('[AGORA] Using preferred turn server url', preferredTurnServerUrl);
    } else {
      loggerService.log('[AGORA] Setting up TURN server fallback after proxy exhaustion');
    }

    const routeName = store.state.route.name;
    /*
    This will return two arrays with first being URLs for STUN servers and second array with URLs for TURN servers
    STUN servers help with NAT traversal, TURN servers relay traffic when direct connection fails
    */
    let response: CloudflareTurnTokenResponse;
    if (routeName === 'UngatedTechCheck') {
      response = await createCloudflarePublicTurnTokenCreds({});
    } else {
      response = await createCloudflareAuthenticatedTurnTokenCreds({});
    }

    if (!response) {
      throw new Error(`Failed to fetch TURN credentials`);
    }
    const cloudflrareTurnUrl = preferredTurnServerUrl || response.ice_servers[1].urls.pop();

    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName: channelName,
      updates: {
        turnServerUrl: cloudflrareTurnUrl
      }
    });
    loggerService.log('[AGORA] TURN server credentials obtained using turn server fallback', {
      turnServerUrl: cloudflrareTurnUrl
    });

    client.setTurnServer([
      {
        urls: response.ice_servers[0].urls
      },
      {
        urls: cloudflrareTurnUrl,
        username: response.ice_servers[1].username,
        credential: response.ice_servers[1].credential
      }
    ] as any);
  } catch (error) {
    loggerService.error('[AGORA] Failed to setup TURN server fallback', { error });
    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName: channelName,
      updates: {
        turnServerFallbackStatus: TurnServerFallbackStatus.FAILED,
        turnServerUrl: undefined
      }
    });
    throw error;
  }
}
export function getUserSuffixForAgoraToken(isScreen: Boolean = false) {
  let userSuffix = '';
  if (store.state.event.stage?.isTVMode) {
    userSuffix = userSuffix + AGORA.UID_SUFFIX.TV_SCREEN;
  } else if (store.state.event.stage?.isLiveStreamMode) {
    userSuffix = userSuffix + AGORA.UID_SUFFIX.LIVE_STREAMER;
  }
  if (isScreen) {
    userSuffix = userSuffix + AGORA.UID_SUFFIX.SCREEN_STREAM;
    if (store.state.route.query.record_mode === 'true') {
      userSuffix = userSuffix + AGORA.UID_SUFFIX.RECORDER;
    }
  }
  // 4 digit random suffix to avoid clashes from 2 tabs
  userSuffix = '|' + Math.random().toString(36).slice(2, 6) + userSuffix;
  return userSuffix;
}

async function fetchAttendeeAgoraTokenForChannel(channelName: string) {
  try {
    const channelId = channelName.split('|')[0];
    const agoraTokenFromStrore = store.state.mediaroom.agoraTokens.find(el => el.room == channelId);

    // try to get token from store and return from store if available.
    if (agoraTokenFromStrore) {
      return agoraTokenFromStrore;
    }

    // if token is not available in store just try to get bulk tokens for the user
    /* istanbul ignore next */
    loggerService.track('[API_CONSOLIDATION] agora token cache miss', {
      tokensInStore: store.state.mediaroom.agoraTokens.length,
      channelName
    });
    const tokens = await store.dispatch('mediaroom/fetchBulkAgoraToken');
    const token = tokens?.find((el: { room: string }) => el.room === channelId);
    if (token) {
      return token;
    }
    /* istanbul ignore next */
    loggerService.track('[API_CONSOLIDATION] agora bulk fetch miss - falling back to individual token fetch', {
      channelName
    });
    // even if we dont find token in bulk fetch just fetch it noemally and return token.
    return fetchAgoraToken(channelName, false, true);
  } catch (error) {
    loggerService.error('error getting token from bulk agora tokens', error);
    return fetchAgoraToken(channelName, false, true);
  }
}

export async function fetchAgoraToken(channelName: string, isScreen: Boolean | undefined, isAttendee: Boolean) {
  const userSuffix = getUserSuffixForAgoraToken(isScreen);
  const currentUserId = store.state.user.currentUser?.id;

  const channelSuffix = channelName.split('|')[1] ? '|' + channelName.split('|')[1] : '';
  const response = await getAgoraToken(channelName, userSuffix, currentUserId, isAttendee);
  return {
    token: (response.data as any).token || (response.data as any).key,
    channel_suffix: channelSuffix,
    suffix: userSuffix
  };
}

export async function getAgoraTokentoJoinClient(
  channelName: string,
  isAttendee: boolean,
  isScreen: Boolean | undefined,
  forceFetchFromApi: Boolean = false
) {
  const useConsolidation = store.state.event.useEventApiConsolidation;
  if (isAttendee && !forceFetchFromApi) {
    loggerService.log('[AgoraToken] bulk-unprivileged path', {
      flow: useConsolidation && !isAttendee ? 'consolidation-flag' : 'original',
      channelName,
      isAttendee,
      useConsolidation
    });
    return fetchAttendeeAgoraTokenForChannel(channelName);
  }
  return fetchAgoraToken(channelName, isScreen, isAttendee);
}
async function getAgoraTokenWithRetries(
  channelName: string,
  isAttendee: boolean,
  isScreen: Boolean | undefined,
  forceFetchFromApi: Boolean = false
): Promise<AgoraToken> {
  const response: AgoraToken = await retry(
    () => {
      if (store.state.mediaroom.mediaRooms?.[channelName]?.desiredStatus !== 'CONNECTED') {
        return Promise.reject('Fail to fetch Agora Token');
      }

      return getAgoraTokentoJoinClient(channelName, isAttendee, isScreen, forceFetchFromApi);
    },
    5,
    1000
  );
  return new Promise(resolve => {
    resolve(response);
  });
}

/* c8 ignore next */
function subscribeMediaRoomEvents(mediaRoom: MediaRoom) {
  const currentUserId = store.state.user.currentUser?.id;
  const shouldSubscribe = (uid: string, mediaType?: string) => {
    if (store.state.event.stage?.isLiveStreamMode) {
      return false;
    }
    if (
      store.state.event.stage?.isTVMode &&
      uid.includes(AGORA.UID_SUFFIX.LIVE_STREAMER) &&
      uid.includes(currentUserId || '')
    ) {
      return false;
    }

    if (
      mediaType === 'audio' &&
      uid.toString().includes(AGORA.UID_SUFFIX.SCREEN_STREAM) &&
      uid.toString().includes(store.state.user.currentUser?.id || '')
    ) {
      // Don't play the audio locally for the user who is sharing
      return false;
    }
    return true;
  };

  mediaRoom.screenShareClient.on(
    'connection-state-change',
    async (
      currentState: ConnectionState,
      previousState: ConnectionState,
      connectionDisconnectedReason?: ConnectionDisconnectedReason
    ) => {
      loggerService.warn(`agora-screen-client-connection-state ${previousState} -> ${currentState}`);

      if (currentState === 'DISCONNECTED') {
        if (connectionDisconnectedReason != 'LEAVE') {
          trackerService.track('agora', 'disconnections');
        }

        loggerService.warn('Agora screen client connection disconnected', {
          connectionDisconnectedReason
        });
      }

      try {
        const response = await store.dispatch('mediaroom/handleConnectionStateChangeForScreen', {
          currentState,
          previousState,
          channelName: mediaRoom.channelName,
          connectionDisconnectedReason
        });
        updateSDRecordingStatus('screenshare', mediaRoom.channelName, true, connectionDisconnectedReason);
        return response;
      } catch (error) {
        loggerService.error('Error handling screen connection state change', error);
      }
    }
  );

  peerConnectionStartTimes.set(mediaRoom.channelName, Date.now());
  mediaRoom.client.on(
    AGORA_EVENTS.PEER_CONNECTION_STATE_CHANGE,
    (currentPeerConnectionState: RTCPeerConnectionState, previousPeerConnectionState: RTCPeerConnectionState) => {
      const currentState = store.state.mediaroom.mediaRooms[mediaRoom.channelName]?.status;
      loggerService.warn(
        `agora-peer-connection-state ${previousPeerConnectionState} -> ${currentPeerConnectionState}`,
        {
          channelName: mediaRoom.channelName
        }
      );
      store.commit('mediaroom/updateChannelPeerConnectionState', {
        channelName: mediaRoom.channelName,
        updates: {
          peerConnectionState: currentPeerConnectionState
        }
      });

      updateSDRecordingStatus('user', mediaRoom.channelName, false);

      if (currentPeerConnectionState === 'connected') {
        const channelKey = mediaRoom.channelName;
        const timeout = peerConnectionTimeouts.get(channelKey);

        if (timeout) {
          clearTimeout(timeout);
          peerConnectionTimeouts.delete(channelKey);
        }

        verifyAndSaveProxyMode(mediaRoom.channelName);
        const startTime = peerConnectionStartTimes.get(channelKey);
        const timeToSuccess = startTime ? Date.now() - startTime : null;
        if (timeToSuccess !== null) {
          peerConnectionStartTimes.delete(channelKey);
        }
        if (store.state.event.useCloudProxyFlow) {
          analytics.track('agora_peer_connection_success', {
            ...getTrackingValues(mediaRoom),
            connectionStatus: currentState,
            peerConnectionStatus: currentPeerConnectionState,
            timeToSuccessMs: timeToSuccess
          });
        }
      }
    }
  );

  mediaRoom.client.on(
    'connection-state-change',
    async (
      currentState: ConnectionState,
      previousState: ConnectionState,
      connectionDisconnectedReason?: ConnectionDisconnectedReason
    ) => {
      loggerService.warn(`agora-client-connection-state ${previousState} -> ${currentState}`);
      store.commit('mediaroom/updateChannelPeerConnectionState', {
        channelName: mediaRoom.channelName,
        updates: {
          status: currentState
        }
      });

      const currentBroadcast: Broadcast = store.getters['event/currentBroadcast'];

      if (currentState === 'DISCONNECTED') {
        if (connectionDisconnectedReason != 'LEAVE') {
          trackerService.track('agora', 'disconnections');
          noOfAgoraDisconnectioned++;
        }

        analytics.track('agora-disconnected', {
          user_id: currentUserId,
          broadcast_id: currentBroadcast?.id,
          discussion_room_id: store.state.route.name == 'DiscussionRoom' ? store.state.route.params?.id : '',
          event_id: store.state.event.event?.id,
          reason_for_disconnect: connectionDisconnectedReason,
          agora_user_role: mediaRoom.userRole,
          event_role: store.state.user.currentEventUser?.event_role
        });
        loggerService.warn('Agora client connection disconnected', {
          connectionDisconnectedReason
        });
      }

      if (currentState === 'CONNECTED') {
        const channelKey = mediaRoom.channelName;

        const existing = peerConnectionTimeouts.get(channelKey);
        if (existing) {
          clearTimeout(existing);
          peerConnectionTimeouts.delete(channelKey);
        }

        const timeout = setTimeout(() => {
          const latestPeerConnectionState =
            store.state.mediaroom.channelPeerConnectionStates[channelKey]?.peerConnectionState || 'new';

          if (latestPeerConnectionState === 'connected') {
            peerConnectionTimeouts.delete(channelKey);
            return;
          }

          handlePeerConnectionTimeout(channelKey);
          peerConnectionTimeouts.delete(channelKey);
        }, AGORA_CONNECTION_TIMEOUT);

        peerConnectionTimeouts.set(channelKey, timeout);
      }

      try {
        const response = await store.dispatch('mediaroom/handleConnectionStateChange', {
          currentState,
          previousState,
          channelName: mediaRoom.channelName,
          connectionDisconnectedReason
        });
        updateSDRecordingStatus('user', mediaRoom.channelName, false, connectionDisconnectedReason);
        return response;
      } catch (error) {
        loggerService.error('Error handling connection state change', error);
      }
    }
  );

  const REMOTE_USER_SUBSCRIBE_MAX_ATTEMPTS = 5;
  const RETRY_BASE_DELAY = 333;
  mediaRoom.client.on('user-published', async (user: IAgoraRTCRemoteUser, mediaType: ITrack['trackMediaType']) => {
    if (!shouldSubscribe(<string>user.uid, mediaType)) {
      return;
    }
    if (mediaType === 'video' && mediaRoom.channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
      if (store.state.event.stage?.greenRoomVideoMuted) {
        return false;
      }
    }

    let currentSubscribeAttempt = 0;
    const remoteUserId = String(user.uid).split('|')[0];
    while (currentSubscribeAttempt < REMOTE_USER_SUBSCRIBE_MAX_ATTEMPTS) {
      try {
        await mediaRoom.client.subscribe(user, mediaType);

        // On success, remove from failing list to prevent future false-positive notifications.
        if (failingSubscriptionUids.has(remoteUserId)) {
          failingSubscriptionUids.delete(remoteUserId);
        }

        return store.dispatch('mediaroom/addRemoteUserTrack', {
          user,
          mediaType,
          channelName: mediaRoom.channelName
        });
      } catch (err) {
        const rtcError = err as IAgoraRTCError;
        const ignoredErrors = ['INVALID_REMOTE_USER', 'UNEXPECTED_RESPONSE'];

        if (ignoredErrors.includes(rtcError.code)) {
          loggerService.warn('Not a valid user, skipping subscribe attempt');
          return;
        }
        currentSubscribeAttempt++;
        loggerService.warn(`Attempt ${currentSubscribeAttempt} to subscribe to ${user.uid} failed.`, err);

        // Stop retrying after last attempt
        if (currentSubscribeAttempt >= REMOTE_USER_SUBSCRIBE_MAX_ATTEMPTS) {
          loggerService.error(
            `Max subscribe retries reached, rejoining room ${mediaRoom.channelName} for user ${user.uid}`
          );

          // Check if main stage user is trying to subscribe to backstage user - skip rejoin in this case
          const currentChannelIsBackstage = mediaRoom.channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE);
          const mainStageChannelId = Array.isArray(store.state.route.params?.id)
            ? store.state.route.params.id[0]
            : store.state.route.params?.id;
          const mainStageRoom = store.state.mediaroom.mediaRooms[mainStageChannelId];
          const isCurrentUserMainStageUser = mainStageRoom?.userRole === MediaUserRole.host;

          const shouldSkipRejoin = isCurrentUserMainStageUser && currentChannelIsBackstage;

          if (failingSubscriptionUids.has(remoteUserId)) {
            // This is the second failure (after rejoining), show notification and stop.
            failingSubscriptionUids.delete(remoteUserId);
            loggerService.error(`Still failing to subscribe to ${user.uid} after rejoining.`);
            if (mediaRoom.userRole === MediaUserRole.audience) {
              NotificationService.toast(
                '@@translate@@ Connection Issue',
                '@@translate@@ We are unable to connect you to the stream. Please refresh the page or switch to the lite tab.',
                'warning'
              );
            } else {
              NotificationService.toast(
                '@@translate@@ Connection Issue',
                '@@translate@@ We are unable to connect you to the stream. Please refresh the page',
                'warning'
              );
            }
          } else if (shouldSkipRejoin) {
            failingSubscriptionUids.add(remoteUserId);
            loggerService.warn(
              `Main stage user failed to subscribe to backstage user ${user.uid}. Skipping room rejoin to avoid disrupting main stage.`
            );
            NotificationService.toast(
              '@@translate@@ Connection Issue',
              '@@translate@@ Unable to connect to backstage stream, please refresh the page',
              'warning'
            );
          } else {
            // First time hitting max failures, add to list and rejoin room to recover silently.
            failingSubscriptionUids.add(remoteUserId);
            loggerService.warn(
              `Max subscribe retries reached for ${user.uid}. Rejoining room ${mediaRoom.channelName} to recover.`
            );
            await store.dispatch('mediaroom/exitRoom', { channelName: mediaRoom.channelName });
            await store.dispatch('mediaroom/joinRoom', {
              channelName: mediaRoom.channelName,
              role: mediaRoom.userRole,
              mode: mediaRoom.mode,
              forceFetchFromApi: true
            });
            return;
          }
        }
        const backoff = Math.pow(2, currentSubscribeAttempt) * RETRY_BASE_DELAY;
        await new Promise(resolve => setTimeout(resolve, backoff));
      }
    }
  });

  mediaRoom.client.on('user-unpublished', (user: IAgoraRTCRemoteUser, mediaType: ITrack['trackMediaType']) => {
    if (!shouldSubscribe(<string>user.uid, mediaType)) {
      return;
    }
    return store.dispatch('mediaroom/removeRemoteUserTrack', {
      user,
      mediaType,
      channelName: mediaRoom.channelName
    });
  });

  mediaRoom.client.on('user-joined', (user: IAgoraRTCRemoteUser) => {
    if (!shouldSubscribe(<string>user.uid)) {
      return;
    }
    // Disable Fallback to Audio Only streams under low network (can enable for DRs)
    mediaRoom.client.setStreamFallbackOption(user.uid, 0);
    return store.dispatch('mediaroom/addRemoteUser', {
      user,
      channelName: mediaRoom.channelName
    });
  });

  mediaRoom.client.on('user-left', (user: IAgoraRTCRemoteUser) => {
    if (!shouldSubscribe(<string>user.uid)) {
      return;
    }
    return store.dispatch('mediaroom/removeRemoteUser', {
      user,
      channelName: mediaRoom.channelName
    });
  });

  mediaRoom.client.on('network-quality', (networkQuality: NetworkQuality) => {
    collectNetworkQualityDataPoints(networkQuality);
    return store.dispatch('mediaroom/checkAndUpdateLocalNetworkQuality', {
      networkQuality
    });
  });

  mediaRoom.client.on('join-fallback-to-proxy', proxyServer => {
    loggerService.warn('Fallback to proxy server', proxyServer);
  });

  mediaRoom.client.on('is-using-cloud-proxy', bool => {
    setUsingCloudProxy(bool);
    loggerService.warn('Using cloud proxy', bool);
  });

  let lastActiveSpeakerId: string | null = null;
  let lastSpokenAt = 0;

  const SILENCE_HOLD_DURATION = 1500; // ms

  mediaRoom.client.enableAudioVolumeIndicator();
  mediaRoom.client.on('volume-indicator', volumes => {
    const VOLUME_SENSITIVITY = 3;
    const now = Date.now();

    let maxAudioLevelUID: string | undefined;
    let maxRemoteAudioLevel = 0;

    // 1️⃣ Detect loudest remote speaker
    volumes.forEach(volume => {
      if (volume.level > maxRemoteAudioLevel && volume.level > VOLUME_SENSITIVITY) {
        maxAudioLevelUID = volume.uid.toString();
        maxRemoteAudioLevel = volume.level;
      }
    });

    // 2️⃣ Compare with local speaker
    store.state.mediaroom.localTracks.forEach((localTrack: LocalTrack) => {
      if (
        store.state.mediaroom.localAudioLevel &&
        localTrack.trackStatus === LocalTrackStatus.PUBLISHED &&
        store.state.mediaroom.localAudioLevel > maxRemoteAudioLevel
      ) {
        maxAudioLevelUID = currentUserId;
      }
    });

    loggerService.info('Active speaker detection', {
      detectedSpeaker: maxAudioLevelUID,
      channelName: mediaRoom.channelName,
      maxRemoteAudioLevel,
      localAudioLevel: store.state.mediaroom.localAudioLevel,
      lastActiveSpeakerId
    });

    // 3️⃣ Decision logic (IMPORTANT PART)
    if (maxAudioLevelUID) {
      // Someone is actively speaking
      lastActiveSpeakerId = maxAudioLevelUID;
      lastSpokenAt = now;

      store.commit('mediaroom/setActiveSpeakerId', {
        channelName: mediaRoom.channelName,
        activeSpeakerId: maxAudioLevelUID
      });
    } else if (lastActiveSpeakerId && now - lastSpokenAt < SILENCE_HOLD_DURATION) {
      // Silence frame → preserve last speaker
      store.commit('mediaroom/setActiveSpeakerId', {
        channelName: mediaRoom.channelName,
        activeSpeakerId: lastActiveSpeakerId
      });
    }
  });

  mediaRoom.client.on('exception', exception => {
    trackerService.track('agora', 'error');
    loggerService.warn(`Agora client exception ${exception.code} ${exception.msg}`, { uid: exception.uid });

    if ((<string>exception.uid).includes(currentUserId || '')) {
      handleException(
        mediaRoom,
        exception.code,
        exception.uid as string,
        (mediaRoom.client as any)._cloudProxyServerMode === 'enabled'
      );
    }
  });

  mediaRoom.screenShareClient.on('exception', exception => {
    trackerService.track('agora', 'error');
    loggerService.warn(`Agora screen client exception ${exception.code} ${exception.msg}`, { uid: exception.uid });
  });
}

function unsubscribeMediaRoomEvents(mediaRoom: MediaRoom) {
  mediaRoom?.client?.removeAllListeners();
  mediaRoom?.screenShareClient?.removeAllListeners();
}

export function closeJoiningDelayNetworkErrorModal() {
  const dialogReferenceIndex = store.state.mediaroom.networkErrorIndex;
  if (dialogReferenceIndex !== undefined) {
    DialogService.close(dialogReferenceIndex);
    store.commit('mediaroom/setNetworkErrorIndex', undefined);
  }
}

async function handleJoiningDelay(channelName: string) {
  const channelStorageKey = `${LOCAL_STORAGE_KEYS.hasShownJoiningDelayNetworkErrorModal}_${channelName}`;
  if (getValueFromSessionStorage(channelStorageKey)) {
    loggerService.info('Joining delay network error modal has already been shown for channel', { channelName });
    return;
  }
  saveValueToSessionStorage(channelStorageKey, true);
  const isAttendee = store.state.user.currentEventUser?.event_role === UserRole.EVENT_MEMBER;
  const isTechCheck = ['SpeakerTechSetup', 'AttendeeTechSetup', 'UngatedTechCheck'].includes(
    <string>(store.state.route.name || '')
  );
  closeJoiningDelayNetworkErrorModal();
  analytics.track('joining_delay_network_error_modal', {
    isAttendee: isAttendee,
    isTechCheck: isTechCheck
  });
  const { index } = DialogService.show(JoiningDelayDailog, {
    size: 'small',
    isTechCheck: isTechCheck,
    isAttendee: isAttendee
  });
  store.commit('mediaroom/setNetworkErrorIndex', index);
}

/* c8 ignore next */
async function handleJoinChannel(mediaRoom: MediaRoom, isScreen?: boolean, forceFetchTokenFromApi: Boolean = false) {
  const { channelName, id } = mediaRoom;
  const currentUserId = store.state.user.currentUser?.id;

  if (!currentUserId) {
    loggerService.warn('handleJoinChannel: currentUserId is undefined');
    return;
  }

  const userRole = isScreen ? MediaUserRole.host : mediaRoom.userRole;
  const client = isScreen ? mediaRoom.screenShareClient : mediaRoom.client;

  const progressiveCloudProxyMode =
    store.state.mediaroom.channelPeerConnectionStates?.[channelName]?.progressiveCloudProxyMode;
  const preferredCloudProxyMode = parseInt(getValueFromSessionStorage(LOCAL_STORAGE_KEYS.preferredCloudProxyMode));
  const isTurnServerFallbackInProgress =
    store.state.mediaroom.channelPeerConnectionStates?.[channelName]?.turnServerFallbackStatus === 'IN_PROGRESS';
  const preferredTurnServerUrl = getValueFromSessionStorage(LOCAL_STORAGE_KEYS.preferredTurnServerUrl);

  if (progressiveCloudProxyMode !== undefined && progressiveCloudProxyMode !== 'turn_server_fallback') {
    loggerService.log('[AGORA] Using progressive cloud proxy mode', progressiveCloudProxyMode);
    client.startProxyServer(progressiveCloudProxyMode);
  } else if (isTurnServerFallbackInProgress || preferredTurnServerUrl) {
    try {
      analytics.track('progressive-cloud-proxy-attempt', {
        ...getTrackingValues(mediaRoom),
        channelName,
        reason: 'preferred-cloud-proxy-mode',
        proxyMode: 'turn_server_fallback'
      });
      await setupTurnServerFallback(client, channelName, preferredTurnServerUrl);
    } catch (error) {
      loggerService.error('[AGORA] Failed to setup TURN server fallback in handleJoinChannel', { error });
    }
  } else if (preferredCloudProxyMode) {
    store.commit('mediaroom/updateChannelPeerConnectionState', {
      channelName,
      updates: {
        progressiveCloudProxyMode: preferredCloudProxyMode,
        isProgressiveCloudProxyStatus: ProgressiveCloudProxyStatus.IN_PROGRESS
      }
    });
    analytics.track('progressive-cloud-proxy-attempt', {
      ...getTrackingValues(mediaRoom),
      channelName,
      reason: 'preferred-cloud-proxy-mode',
      proxyMode: preferredCloudProxyMode
    });
    loggerService.warn('[AGORA] Using stored preferred cloud proxy mode', preferredCloudProxyMode);
    client.startProxyServer(preferredCloudProxyMode);
  }

  const requiresConnect = () => {
    const channelRequiresConnect =
      store.state.mediaroom.mediaRooms[channelName]?.desiredStatus === 'CONNECTED' &&
      id === store.state.mediaroom.mediaRooms[channelName]?.id;

    if (isScreen) {
      return (
        channelRequiresConnect &&
        store.state.mediaroom.screenTracks.find((track: LocalTrack) => track.channelName === channelName)
      );
    }

    return channelRequiresConnect;
  };

  const isBackstage = channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE);
  const joiningDelayTimeout = setTimeout(
    async () => {
      if (!requiresConnect()) {
        return;
      }

      if (
        ['SpeakerTechSetup', 'AttendeeTechSetup', 'UngatedTechCheck'].includes(<string>(store.state.route.name || ''))
      ) {
        return;
      }

      handleJoiningDelay(channelName);
    },
    isBackstage ? 12000 : 10000
  );

  trackerService.track('agora', 'connectionInitialized', { timestamp: Date.now() });
  try {
    const { token, suffix } = await getAgoraTokenWithRetries(
      channelName,
      userRole === MediaUserRole.audience,
      isScreen,
      forceFetchTokenFromApi
    );

    await client.join(AGORA.APP_ID, channelName, token, currentUserId + suffix);
    analytics.track('join_media_room', {
      ...getTrackingValues(mediaRoom)
    });
    trackerService.track('agora', 'connectionEstablished', { timestamp: Date.now() });

    logAgoraUserMetadata(store.state.user.currentEventUser, userRole, suffix, channelName);

    toggleRemoteSignalTracking({ mediaRoom, enable: true });

    clearTimeout(joiningDelayTimeout);

    closeJoiningDelayNetworkErrorModal();

    if (!requiresConnect()) {
      await client.leave();
      if (id === store.state.mediaroom.mediaRooms[channelName]?.id) {
        await store.dispatch('mediaroom/exitRoom', { channelName });
      }
      return;
    }

    loggerService.warn('agora-join-channel');

    if (userRole === MediaUserRole.host && mediaRoom.mode === AGORA.MODE.LIVE) {
      await setRole(client, channelName, MediaUserRole.host);
    }
    const dispatchAction = isScreen ? 'mediaroom/checkAndPublishScreenToChannel' : 'mediaroom/checkAndPublishToChannel';
    store.dispatch(dispatchAction, { channelName });
  } catch (error) {
    /* istanbul ignore next */
    clearTimeout(joiningDelayTimeout);
    analytics.track('join_media_room_failed', {
      ...getTrackingValues(mediaRoom),
      error: error
    });
    if (requiresConnect()) {
      if (clientJoinRetries < 3) {
        trackerService.track('agora', 'retry');
        clientJoinRetries = clientJoinRetries + 1;
        setTimeout(() => {
          handleJoinChannel(mediaRoom, isScreen, true);
        }, 2000);
      } else {
        /* c8 ignore start */
        loggerService.info('[PAGE_RELOAD] Reloading after Agora channel join failed all retries', {
          channelName,
          isScreen,
          clientJoinRetries
        });
        window.location.reload();
        /* c8 ignore stop */
      }
    } else {
      closeJoiningDelayNetworkErrorModal();
    }
    loggerService.captureException(error as Error);
  }
}

export function toggleRemoteSignalTracking({ mediaRoom, enable }: { mediaRoom: MediaRoom; enable: boolean }) {
  if (!mediaRoom) {
    return;
  }

  const isBroadcast = store.state.route.path.includes('/broadcast');
  const eventRole = store.state.user.currentEventUser?.event_role as UserRole;

  if ([UserRole.EVENT_MEMBER, UserRole.RECORDER_BOT, UserRole.SPONSOR].includes(eventRole) && isBroadcast) {
    return;
  }

  if (remoteSignalTrackerInterval) {
    clearInterval(remoteSignalTrackerInterval);
    remoteSignalTrackerInterval = null;
    store.commit('mediaroom/setRemoteSignalMetadata', {
      remoteNetworkQuality: {}
    });
  }

  if (enable) {
    remoteSignalTrackerInterval = setInterval(() => {
      const mediaRooms = store.state.mediaroom.mediaRooms;
      const mergedNetworkQuality = {};

      Object.values(mediaRooms).forEach(room => {
        if (room.client?.connectionState === 'CONNECTED') {
          Object.assign(mergedNetworkQuality, room.client.getRemoteNetworkQuality());
        }
      });

      store.commit('mediaroom/setRemoteSignalMetadata', {
        remoteNetworkQuality: mergedNetworkQuality
      });
    }, 5000);
  }
}

export async function joinMediaRoom(
  channelName: MediaRoom['channelName'],
  userRole: MediaUserRole,
  mode: SDK_MODE,
  forceFetchFromApi?: boolean
): Promise<MediaRoom> {
  trackerService.track('agora', 'shouldBeUsed', { isEnabled: true });
  const mediaRoom = await createMediaRoom(channelName, userRole, mode);

  subscribeMediaRoomEvents(mediaRoom);
  handleJoinChannel(mediaRoom, false, forceFetchFromApi);
  return mediaRoom;
}

export function shareScreen(mediaRoom: MediaRoom) {
  handleJoinChannel(mediaRoom, true);
}

export async function setRole(client: IAgoraRTCClient, channelName: string, userRole: MediaRoom['userRole']) {
  const currentUserId = store.state.user.currentUser?.id;

  if (!channelName || !currentUserId) {
    return;
  }
  if (currentUserHasStageRequest(channelName) && userRole === MediaUserRole.host) {
    updateOnStageRequest(currentUserId, 'COMPLETED');
  }
  await client.setClientRole(userRole);
  if (userRole === MediaUserRole.host) {
    syncUserUidsWithBackend(client);
  }
}

export async function publishLocalTracks(client: IAgoraRTCClient, tracks: ILocalTrack[]) {
  try {
    const statictTracks = tracks.map(track => {
      return trackMap.get(track.getTrackId()) || track;
    });
    await client.publish(statictTracks);
    loggerService.warn('agora-publish-local-tracks');
  } catch (error) {
    loggerService.captureException(error as Error);
    throw error;
  }
}

export async function unpublishLocalTracks(client: IAgoraRTCClient, tracks: ILocalTrack[]): Promise<void> {
  try {
    tracks.forEach(track => {
      trackMap.delete(track.getTrackId());
    });
    await client.unpublish(tracks);
    loggerService.warn('agora-unpublish-local-tracks');
  } catch (error) {
    loggerService.captureException(error as Error);
    throw error;
  }
}

export async function exitMediaRoom(mediaRoom: MediaRoom): Promise<void> {
  try {
    unsubscribeMediaRoomEvents(mediaRoom);
    await mediaRoom?.client?.leave();
    await mediaRoom?.screenShareClient?.leave();
    analytics.track('exit_media_room', {
      ...getTrackingValues(mediaRoom)
    });
    toggleRemoteSignalTracking({ mediaRoom, enable: false });
    loggerService.warn('exited-media-room');
  } catch (error) {
    analytics.track('exit_media_room_failed', {
      ...getTrackingValues(mediaRoom),
      error: error
    });
    loggerService.captureException(error as Error);
    throw error;
  }
}

export function handleException(mediaRoom: MediaRoom, code: Number, uid: string, isCloudProxy: boolean) {
  if (code === 2001) {
    if (noOfLowAudioNotifications < NOTIFICATIONS_THRESHOLD) {
      NotificationService.toast(
        '@@translate@@ Microphone audio too low',
        "@@translate@@ We've detected a problem with your audio. We recommend trying to mute/unmute or use another device if the issue persists.",
        'warning',
        '',
        AGORA.AV_NOTIFICATION_TIMEOUT
      );
    }
    loggerService.warn('agora-exception', {
      channelName: mediaRoom.channelName,
      userRole: mediaRoom.userRole,
      mode: mediaRoom.mode,
      count: noOfLowAudioNotifications,
      code
    });
    noOfLowAudioNotifications++;
    return;
  }
  if (code === 4001) {
    noOfLowAudioNotifications--;
    loggerService.warn('agora-exception', {
      channelName: mediaRoom.channelName,
      userRole: mediaRoom.userRole,
      mode: mediaRoom.mode,
      count: noOfLowAudioNotifications,
      code
    });
    return;
  }

  if (code === 3003 || code === 4003) {
    noOfSendLowBitrateNotifications--;
    loggerService.warn('agora-exception', {
      channelName: mediaRoom.channelName,
      userRole: mediaRoom.userRole,
      mode: mediaRoom.mode,
      count: noOfSendLowBitrateNotifications,
      code
    });
  }

  if (code === 2003 || code === 1003) {
    if (noOfSendLowBitrateNotifications < NOTIFICATIONS_THRESHOLD) {
      NotificationService.toast(
        '@@translate@@ Video/Audio quality is low',
        "@@translate@@ We've detected that your video/audio bitrate is too low, which may affect video/audio quality. Please check your internet connection, close other bandwidth-intensive applications, or try switching to a different network.",
        'warning',
        '',
        AGORA.AV_NOTIFICATION_TIMEOUT
      );
    }
    loggerService.warn('agora-exception', {
      channelName: mediaRoom.channelName,
      userRole: mediaRoom.userRole,
      mode: mediaRoom.mode,
      count: noOfSendLowBitrateNotifications,
      code
    });
    noOfSendLowBitrateNotifications++;

    if (isCloudProxy) {
      NotificationService.toast(
        '@@translate@@ Audio/Video bitrate too low',
        '@@translate@@ Your network firewall settings are blocking the A/V streams. We recommend that you switch to a different network connection.',
        'warning'
      );
    }
    return;
  }

  if (code === 1005 && uid.includes(AGORA.UID_SUFFIX.SCREEN_STREAM)) {
    DialogService.show(
      GenericMessageDialog,
      {
        title: '@@translate@@ Error sharing screen',
        message: '@@translate@@ A network error occurred while sharing screen. Please check your network connection.',
        noCancel: true
      },
      {
        cancelOnClickOutside: false
      }
    );
    return;
  }
}

function logAgoraUserMetadata(
  currentEventUser: User | undefined,
  mediaUserRole: MediaUserRole,
  userSuffix: string,
  channelName: string
) {
  try {
    analytics.track('agora-user', {
      user_id: currentEventUser?.id,
      user_suffix: userSuffix.split('|')[1],
      channel_id: channelName.split('|')[0],
      channel_suffix: channelName.split('|')[1],
      user_type: currentEventUser?.eventUser?.event_role,
      email: currentEventUser?.email,
      media_user_role: mediaUserRole
    });
  } catch (error) {
    loggerService.warn('Error tracking agora user metadata', { error: classifyDiagnosticError(error, 'unknown') });
  }
}

export function isAnyMediaRoomReconnecting() {
  const mediaRooms = store.state.mediaroom?.mediaRooms;
  let isReconnecting = false;
  Object.keys(mediaRooms).forEach(roomId => {
    const room: MediaRoom = mediaRooms[roomId];
    if (room.desiredStatus !== 'CONNECTED') {
      return;
    }
    if (['RECONNECTING', 'DISCONNECTED'].includes(room.status || '')) {
      isReconnecting = true;
    }
  });

  return isReconnecting;
}

const sharedIntUids: number[] = [];

async function syncUserUidsWithBackend(client: IAgoraRTCClient & any) {
  try {
    const backendHaUrl = getEnv('VUE_APP_BACKEND_HA_URL');
    if (!backendHaUrl) {
      loggerService.error('VUE_APP_BACKEND_HA_URL is not configured');
      return;
    }
    const coreUserId = store.state.user.currentUser?.id;
    const intUid = client?._joinInfo?.uid;
    const stringUid = client?._joinInfo?.stringUid;
    const channel = client?._joinInfo?.cname;

    const isScreen = stringUid.includes(AGORA.UID_SUFFIX.SCREEN_STREAM);
    if (!sharedIntUids.includes(intUid)) {
      sharedIntUids.push(intUid);

      const userData = [
        {
          agora_uid: intUid,
          broadcast_id: channel,
          core_user: {
            id: coreUserId || ''
          },
          core_user_id: String(coreUserId || ''),
          stream_type: isScreen ? ('SCREENSHARE' as StreamTypeEnumApi) : ('SPEAKER' as StreamTypeEnumApi)
        }
      ];

      exponentialRetry(
        async () => {
          try {
            return await backendHaFetcher({
              url: '/user/agora/bulk_create/',
              method: 'POST',
              body: userData,
              queryParams: {
                broadcast: channel
              }
            });
          } catch (error: any) {
            const status = error?.status || error?.response?.status || error?.statusCode;
            if (status && status >= 400 && status < 500) {
              // 4xx errors are client-side and won't resolve on retry
              loggerService.error('Non-retryable error syncing uid data', error);
              return;
            }
            loggerService.error('Retryable error while syncing uid data', error);
            // Network errors (TypeError: Failed to fetch), timeouts (AbortError),
            // and 5xx errors are transient — re-throw so exponentialRetry can retry
            throw error;
          }
        },
        {
          retries: 5,
          delayMs: 5000,
          onRetry: (error, attempt) =>
            loggerService.warn(`Bulk create user data to video editor service retry attempt ${attempt}/5`, error),
          onMaxReached: error =>
            loggerService.error('Bulk create user data to video editor service failed after 5 retries', error)
        }
      );
    }
  } catch (e) {
    loggerService.error(`Error syncing uid data to video editor service`, e);
  }
}
